/**
 * Sign-in for the fleet command centre.
 *
 * The animation is a live telemetry mesh -- asset nodes pulsing across the backdrop, signal
 * packets travelling the links between them, slow radar rings breathing behind the card and
 * a heartbeat trace running underneath. It is deliberately geography-free: this console
 * fronts any deployment of the platform, so the hero says "live operations network", never
 * one particular road or fleet.
 *
 * EVERY ANIMATION RESPECTS prefers-reduced-motion. This is the first screen a person sees
 * and the one they cannot skip, so none of it is load bearing and all of it switches off.
 *
 * Two ways in: Microsoft (Entra OIDC redirect, the primary) and a local username/password
 * against POST /api/auth/local-login. Both end the same way -- the server sets the session
 * cookie and useSession re-reads /api/me; the local form just does it via reload instead of
 * a redirect round-trip.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import transvoltMark from '../assets/transvolt-mark.svg'
import { expandLoginHint, localLoginUsername, microsoftLoginParams } from './loginHint'

/** Telemetry-mesh geometry in the backdrop SVG's 1440x900 viewBox. Nodes sit clear of the
 *  centred card (roughly x 520-920, y 180-720) so the mesh frames the sign-in rather than
 *  fighting it. `d` staggers each node's pulse so the field never beats in unison. */
const NODES = [
  { x: 180, y: 190, d: 0 },
  { x: 120, y: 500, d: 1.1 },
  { x: 330, y: 760, d: 2.3 },
  { x: 430, y: 400, d: 0.6 },
  { x: 720, y: 80, d: 1.7 },
  { x: 1260, y: 210, d: 0.3 },
  { x: 1010, y: 330, d: 2.0 },
  { x: 1330, y: 560, d: 1.4 },
  { x: 1080, y: 780, d: 2.7 },
]

/** [from node, to node, packet-flow duration s, delay s] -- durations and delays are all
 *  mutually offset so no two links ever visibly sync up. */
const LINKS: Array<[number, number, number, number]> = [
  [0, 3, 4.2, 0],
  [0, 1, 5.6, 1.3],
  [1, 3, 4.8, 2.1],
  [1, 2, 5.2, 0.7],
  [2, 3, 6.0, 2.8],
  [0, 4, 5.0, 1.8],
  [4, 5, 5.4, 0.4],
  [5, 6, 4.4, 2.4],
  [6, 7, 4.6, 1.0],
  [7, 8, 5.8, 3.1],
  [6, 8, 5.0, 0.2],
]

/** The heartbeat trace along the bottom of the backdrop: a flat line that spikes at
 *  irregular intervals, like a healthy signal feed. */
const WAVE =
  'M-40 828h260l16-30 16 30h180l14-46 12 46h210l18-24 14 24h190l12-38 12 38h230l16-28 14 28h220'

export function LoginPage({ authError, domain }: { authError?: string; domain: string }) {
  const [busy, setBusy] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [localBusy, setLocalBusy] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [localError, setLocalError] = useState<string | undefined>()
  // Bumped on every failed attempt so the .err banner remounts and its shake replays --
  // a second wrong password with a static banner looks like the click did nothing.
  const [errNonce, setErrNonce] = useState(0)
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 40)
    return () => clearTimeout(t)
  }, [])

  const signIn = () => {
    setBusy(true)
    const qs = microsoftLoginParams(
      window.location.origin,
      window.location.pathname,
      window.location.search,
    )
    const hint = expandLoginHint(username, domain)
    if (hint) qs.set('login_hint', hint)
    window.location.href = `/auth/login?${qs.toString()}`
  }

  const signInLocal = async (e: FormEvent) => {
    e.preventDefault()
    if (busy || localBusy) return
    setLocalBusy(true)
    setLocalError(undefined)
    try {
      const r = await fetch('/api/auth/local-login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: localLoginUsername(username, domain), password }),
      })
      if (r.ok) {
        // The server has set the session cookie. Reload so useSession re-fetches /api/me --
        // the exact path the Microsoft redirect takes back into the console. localBusy stays
        // true so the spinner holds until the new page takes over.
        window.location.reload()
        return
      }
      let msg = 'Sign-in failed. Check your username and password.'
      try {
        const body: unknown = await r.json()
        if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
          msg = (body as { error: string }).error
        }
      } catch { /* non-JSON error body; keep the generic message */ }
      setLocalError(msg)
      setErrNonce((n) => n + 1)
      setLocalBusy(false)
    } catch {
      setLocalError('Could not reach the sign-in service. Try again.')
      setErrNonce((n) => n + 1)
      setLocalBusy(false)
    }
  }

  return (
    <div className="login-root">
      <style>{CSS}</style>

      {/* Atmosphere. aria-hidden because a screen reader narrating a decorative mesh in
          front of the only control that matters is noise, not information. */}
      <div className="hero" aria-hidden="true">
        <svg className="net" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice">
          {/* Radar rings breathing out from behind the card. */}
          <circle className="ring" cx="720" cy="450" r="110" />
          <circle className="ring r1" cx="720" cy="450" r="110" />
          <circle className="ring r2" cx="720" cy="450" r="110" />
          {/* Mesh links: a faint base line, plus a brighter dash "packet" flowing along it. */}
          {LINKS.map(([a, b, dur, delay], k) => {
            const A = NODES[a]
            const B = NODES[b]
            if (!A || !B) return null
            return (
              <g key={k}>
                <line className="lk" x1={A.x} y1={A.y} x2={B.x} y2={B.y} />
                <line
                  className="flow"
                  x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                  style={{ animationDuration: `${dur}s`, animationDelay: `${delay}s` }}
                />
              </g>
            )
          })}
          {/* Asset nodes: a steady dot plus an expanding, fading halo. */}
          {NODES.map((n, k) => (
            <g key={k}>
              <circle
                className="halo"
                cx={n.x} cy={n.y} r="5"
                style={{ animationDelay: `${n.d}s` }}
              />
              <circle className="dot" cx={n.x} cy={n.y} r="3" />
            </g>
          ))}
          {/* Heartbeat trace along the bottom -- the "feed is live" line. */}
          <path className="wavebase" d={WAVE} />
          <path className="wave" d={WAVE} />
        </svg>
      </div>

      <div className={`card ${mounted ? 'in' : ''}`}>
        <div className="brand">
          <img className="logo" src={transvoltMark} alt="Transvolt" />
          <p className="sub">Command Centre · Live Fleet Operations</p>
        </div>

        {/* role=alert: somebody who has just been refused needs to know why without
            hunting for it. */}
        {authError ? <div className="err" role="alert">{authError}</div> : null}

        <button className="ms" onClick={signIn} disabled={busy || localBusy}>
          {busy ? <span className="spinner" aria-hidden="true" /> : <MicrosoftLogo />}
          <span>{busy ? 'Taking you to Microsoft…' : 'Sign in with Microsoft'}</span>
        </button>

        <div className="or" aria-hidden="true"><span /><em>or</em><span /></div>

        <form className="local" onSubmit={signInLocal}>
          {localError ? <div className="err" role="alert" key={errNonce}>{localError}</div> : null}
          <label className="fld">
            <span className="fld-label">Username</span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="jafar.k"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              disabled={busy || localBusy}
            />
          </label>
          <label className="fld">
            <span className="fld-label">Password</span>
            <div className="pw-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={busy || localBusy}
              />
              <button
                type="button"
                className="pw-toggle"
                onClick={() => setShowPassword((s) => !s)}
                disabled={busy || localBusy}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </label>
          <button className="go" type="submit" disabled={busy || localBusy}>
            {localBusy ? <span className="spinner lite" aria-hidden="true" /> : null}
            <span>{localBusy ? 'Signing in…' : 'Sign in'}</span>
          </button>
        </form>

        <p className="note">
          Restricted to <strong>@{domain}</strong> accounts.
          Short names like <strong>jafar.k</strong> become <strong>jafar.k@{domain}</strong>.
          <br />Settings are limited to the platform administrators.
        </p>
      </div>

      <p className="foot">Live telemetry · Real-time command · Secure access</p>
    </div>
  )
}

/** The Microsoft mark at its official colours. Recognition is this button's whole job, and
 *  a recoloured logo reads as a phishing page. */
function MicrosoftLogo() {
  return (
    <svg width="17" height="17" viewBox="0 0 23 23" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  )
}

const CSS = `
.login-root{position:fixed;inset:0;display:grid;place-items:center;
  background:linear-gradient(160deg,#0B1120 0%,#111C31 45%,#0A1526 100%);
  font-family:'Instrument Sans',system-ui,sans-serif;overflow:hidden}
.login-root::after{content:'';position:absolute;inset:0;pointer-events:none;
  background:radial-gradient(120% 90% at 50% 0%,transparent 40%,rgba(3,7,18,.75) 100%)}
.hero{position:absolute;inset:0;opacity:.5}
.net{width:100%;height:100%;display:block}
.ring{fill:none;stroke:#38BDF8;stroke-width:1.2;opacity:0;
  transform-box:fill-box;transform-origin:center;animation:ripple 7.5s ease-out infinite}
.r1{animation-delay:2.5s}
.r2{animation-delay:5s}
@keyframes ripple{0%{transform:scale(.35);opacity:.32}
  70%{opacity:.06}100%{transform:scale(3.1);opacity:0}}
.lk{stroke:#1E3A5F;stroke-width:1;opacity:.55}
.flow{stroke:#38BDF8;stroke-width:1.5;opacity:.55;stroke-linecap:round;
  stroke-dasharray:16 224;animation:packet 5s linear infinite}
@keyframes packet{to{stroke-dashoffset:-240}}
.dot{fill:#38BDF8;opacity:.85}
.halo{fill:none;stroke:#38BDF8;stroke-width:1.4;opacity:0;
  transform-box:fill-box;transform-origin:center;animation:blip 3.4s ease-out infinite}
@keyframes blip{0%{transform:scale(1);opacity:.45}100%{transform:scale(3);opacity:0}}
.wavebase{fill:none;stroke:#1E3A5F;stroke-width:1;opacity:.45}
.wave{fill:none;stroke:#38BDF8;stroke-width:1.4;opacity:.4;stroke-linecap:round;
  stroke-dasharray:90 380;animation:packet 6.5s linear infinite}
.card{position:relative;z-index:2;width:min(92vw,392px);padding:34px 32px 28px;
  border-radius:18px;background:rgba(15,23,42,.82);backdrop-filter:blur(18px);
  border:1px solid rgba(148,163,184,.16);
  box-shadow:0 24px 60px -12px rgba(0,0,0,.6),0 0 0 1px rgba(255,255,255,.03) inset;
  opacity:0;transform:translateY(14px) scale(.985);
  transition:opacity .6s cubic-bezier(.16,1,.3,1),transform .6s cubic-bezier(.16,1,.3,1)}
.card.in{opacity:1;transform:none}
.brand{display:flex;flex-direction:column;align-items:flex-start;gap:9px;margin-bottom:26px}
.logo{width:172px;height:auto;display:block}
.sub{margin:0;font-family:'JetBrains Mono',monospace;font-size:9.5px;
  letter-spacing:.1em;color:#64748B;text-transform:uppercase}
.ms{width:100%;display:flex;align-items:center;justify-content:center;gap:11px;
  padding:13px 16px;border-radius:11px;border:1px solid rgba(148,163,184,.22);
  background:#F8FAFC;color:#0F172A;font-size:14.5px;font-weight:600;cursor:pointer;
  transition:transform .18s cubic-bezier(.16,1,.3,1),box-shadow .18s,background .18s}
.ms:hover:not(:disabled){background:#fff;transform:translateY(-1.5px);
  box-shadow:0 12px 26px -8px rgba(56,189,248,.4)}
.ms:active:not(:disabled){transform:translateY(0)}
.ms:disabled{opacity:.72;cursor:wait}
.ms:focus-visible{outline:2px solid #38BDF8;outline-offset:3px}
.spinner{width:15px;height:15px;border-radius:50%;border:2px solid rgba(15,23,42,.22);
  border-top-color:#0F172A;animation:spin .7s linear infinite}
.spinner.lite{border-color:rgba(125,211,252,.25);border-top-color:#7DD3FC}
@keyframes spin{to{transform:rotate(360deg)}}
.or{display:flex;align-items:center;gap:12px;margin:20px 0 16px}
.or span{flex:1;height:1px;background:linear-gradient(90deg,transparent,rgba(148,163,184,.22))}
.or span:last-child{background:linear-gradient(90deg,rgba(148,163,184,.22),transparent)}
.or em{font-style:normal;font-family:'JetBrains Mono',monospace;font-size:9px;
  letter-spacing:.14em;color:#475569;text-transform:uppercase}
.local{display:flex;flex-direction:column;gap:12px}
.fld{display:flex;flex-direction:column;gap:5px}
.fld-label{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:.14em;
  color:#64748B;text-transform:uppercase;transition:color .18s}
.fld:focus-within .fld-label{color:#7DD3FC}
.fld input{width:100%;padding:11px 13px;border-radius:10px;font:inherit;font-size:14px;
  color:#F1F5F9;background:rgba(2,6,23,.45);border:1px solid rgba(148,163,184,.18);
  outline:none;transition:border-color .18s,box-shadow .18s,background .18s}
.fld input::placeholder{color:#475569}
.fld input:hover:not(:disabled){border-color:rgba(148,163,184,.3)}
.fld input:focus{border-color:#38BDF8;background:rgba(2,6,23,.6);
  box-shadow:0 0 0 3px rgba(56,189,248,.14)}
.fld input:disabled{opacity:.6;cursor:wait}
.fld input:-webkit-autofill{-webkit-box-shadow:0 0 0 40px #0E1930 inset;
  -webkit-text-fill-color:#F1F5F9;caret-color:#F1F5F9}
.pw-wrap{position:relative}
.pw-wrap input{padding-right:38px}
.pw-toggle{position:absolute;right:4px;top:50%;transform:translateY(-50%);display:flex;
  align-items:center;justify-content:center;width:30px;height:30px;border-radius:7px;
  border:none;background:transparent;color:#64748B;cursor:pointer;transition:color .15s}
.pw-toggle:hover:not(:disabled){color:#CBD5E1}
.pw-toggle:disabled{cursor:wait;opacity:.6}
.pw-toggle:focus-visible{outline:2px solid #38BDF8;outline-offset:1px}
.go{width:100%;display:flex;align-items:center;justify-content:center;gap:10px;margin-top:2px;
  padding:12px 16px;border-radius:11px;border:1px solid rgba(56,189,248,.32);
  background:rgba(56,189,248,.10);color:#7DD3FC;font-size:14px;font-weight:600;cursor:pointer;
  transition:transform .18s cubic-bezier(.16,1,.3,1),box-shadow .18s,background .18s,border-color .18s}
.go:hover:not(:disabled){background:rgba(56,189,248,.16);border-color:rgba(56,189,248,.5);
  transform:translateY(-1.5px);box-shadow:0 12px 26px -8px rgba(56,189,248,.3)}
.go:active:not(:disabled){transform:translateY(0)}
.go:disabled{opacity:.72;cursor:wait}
.go:focus-visible{outline:2px solid #38BDF8;outline-offset:3px}
.local .err{margin-bottom:2px}
.err{margin-bottom:16px;padding:11px 13px;border-radius:9px;font-size:12.5px;line-height:1.45;
  background:rgba(220,38,38,.10);border:1px solid rgba(248,113,113,.28);color:#FCA5A5;
  animation:shake .42s cubic-bezier(.36,.07,.19,.97)}
@keyframes shake{10%,90%{transform:translateX(-1px)}30%,70%{transform:translateX(2px)}
  50%{transform:translateX(-2px)}}
.note{margin:18px 0 0;text-align:center;font-size:11.5px;line-height:1.65;color:#64748B}
.note strong{color:#94A3B8;font-weight:600}
.foot{position:absolute;bottom:20px;z-index:2;margin:0;font-family:'JetBrains Mono',monospace;
  font-size:9px;letter-spacing:.14em;color:#334155;text-transform:uppercase}
@media (prefers-reduced-motion:reduce){
  .ring,.halo,.flow,.wave,.err,.spinner{animation:none}
  .ring{opacity:.1;transform:scale(1.6)}
  .halo{opacity:.2;transform:scale(1.6)}
  .flow{opacity:.3}
  .wave{opacity:.3}
  .card{transition:none;opacity:1;transform:none}
  .ms,.go,.fld input,.fld-label{transition:none}}
@media (max-width:520px){.hero{display:none}}
`
